import { pagesWithin, parsePages } from "./pages.js";
import { layoutQuestions } from "./render.js";
import { OPTION_IDS, OPTIONS_PER_QUESTION, QUESTIONS_PER_SEGMENT, } from "@waypoint/contracts";
import {} from "./types.js";
const { min: MIN_QUESTIONS, max: MAX_QUESTIONS } = QUESTIONS_PER_SEGMENT;
const { min: MIN_OPTIONS, max: MAX_OPTIONS } = OPTIONS_PER_QUESTION;
/**
 * The owner's rules for a question set (HUMAN-GATES row 8, ADR-023 §1): two or three questions per
 * segment, two to four options, exactly one correct, every question cited inside the title's
 * guidePages, and a named beat that exists for the title. Also checks what the type cannot — ids,
 * single-line trimmed text — so a set that arrives as JSON renders to Markdown that parses back
 * to the same set.
 */
export function validateQuestions(check, options) {
    const { file } = options;
    const lines = options.lines ?? layoutQuestions(check).lines;
    const beats = new Set(options.beatIds);
    const errors = [];
    const fail = (code, line, message) => {
        const at = line ?? 1;
        errors.push(file === undefined ? { code, line: at, message } : { code, line: at, message, file });
    };
    const guide = parsePages(options.guidePages);
    if (!guide) {
        fail("guide-pages", 1, `The title's guidePages "${options.guidePages}" are not page references`);
    }
    if (check.segments.length === 0)
        fail("no-segments", 1, "The question set has no segments");
    let previous;
    check.segments.forEach((segment, s) => {
        const segmentLine = lines.segments[s];
        if (!Number.isInteger(segment.n) || segment.n < 1) {
            fail("segment-heading", segmentLine, `Segment number ${String(segment.n)} is not a positive integer`);
        }
        if (previous !== undefined && segment.n <= previous) {
            fail("segment-order", segmentLine, `Segment ${String(segment.n)} follows segment ${String(previous)}`);
        }
        previous = segment.n;
        textFormat(segment.label, "The segment label", segmentLine, fail);
        const count = segment.questions.length;
        if (count < MIN_QUESTIONS || count > MAX_QUESTIONS) {
            fail("segment-question-count", segmentLine, `Segment ${String(segment.n)} has ${String(count)} question${count === 1 ? "" : "s"}; each segment has ${String(MIN_QUESTIONS)} or ${String(MAX_QUESTIONS)}`);
        }
        segment.questions.forEach((q, i) => {
            const at = lines.questions[s]?.[i];
            const expectedId = `${check.titleId}.${String(segment.n)}.${String(i + 1)}`;
            if (q.id !== expectedId) {
                fail("question-id", at?.prompt, `Question id "${q.id}" should be "${expectedId}"`);
            }
            textFormat(q.prompt, "The prompt", at?.prompt, fail);
            const n = q.options.length;
            if (n < MIN_OPTIONS || n > MAX_OPTIONS) {
                fail("option-count", at?.prompt, `The question has ${String(n)} option${n === 1 ? "" : "s"}; a question has ${String(MIN_OPTIONS)} to ${String(MAX_OPTIONS)}`);
            }
            q.options.forEach((option, k) => {
                const expected = OPTION_IDS[k];
                if (expected !== undefined && option.id !== expected) {
                    fail("option-letter", at?.prompt, `Option ${String(k + 1)} is ${option.id}., expected ${expected}.`);
                }
                textFormat(option.text, `Option ${option.id}.`, at?.prompt, fail);
                if (/[✓✔]/.test(option.text) || /^\*\*.*\*\*$/.test(option.text)) {
                    fail("text-format", at?.prompt, `Option ${option.id}. carries its own correct-answer mark`);
                }
            });
            const correct = q.options.filter((option) => option.id === q.answer).length;
            if (correct !== 1) {
                fail("answer-count", at?.prompt, `The answer "${q.answer}" is not exactly one of the options`);
            }
            textFormat(q.why, "The Why: explanation", at?.why, fail);
            // The type makes a citation required; JSON from outside may still lack one, so widen it.
            const citation = q.citation;
            const pages = citation?.pages;
            if (!pages) {
                fail("citation-missing", at?.why, "The question has no guide citation");
            }
            else {
                const cited = parsePages(pages);
                if (!cited) {
                    fail("citation-pages", at?.why, `"${pages}" is not a page reference`);
                }
                else if (guide && !pagesWithin(cited, guide)) {
                    fail("citation-out-of-range", at?.why, `The citation ${pages} falls outside the title's guide pages ${options.guidePages}`);
                }
            }
            if (q.beat !== undefined && !beats.has(q.beat)) {
                fail("beat-unknown", at?.beat ?? at?.prompt, `Beat "${q.beat}" is not a beat of this title`);
            }
        });
    });
    return errors;
}
// Text that is empty, padded or spans lines would not survive renderQuestions → parseQuestions.
function textFormat(value, what, line, fail) {
    if (typeof value !== "string" || value.trim() === "") {
        fail("text-format", line, `${what} is empty`);
    }
    else if (value !== value.trim() || /[\r\n]/.test(value)) {
        fail("text-format", line, `${what} must be one line with no leading or trailing space`);
    }
}
//# sourceMappingURL=validate.js.map